import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { useLang } from "@/lib/i18n";

const HEADS = ["Essential", "Strategic", "Enterprise", "Tailored"];

export default function FeatureMatrix() {
  const { t } = useLang();
  return (
    <section id="coverage" className="bg-white py-24 lg:py-32 border-t border-[#E2E8F0]">
      <div className="max-w-[1320px] mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="eyebrow text-[#C5B358] flex items-center gap-3"
        >
          <span className="w-2 h-2 rotate-45 bg-[#C5B358]" /> {t.matrix.overline}
        </motion.div>
        <motion.h2
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, delay: 0.08 }}
          className="font-display mt-5 text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F1E3D]"
        >
          {t.matrix.titleA}
          <em className="font-serif-accent text-[#C5B358]">{t.matrix.titleEm}</em>
        </motion.h2>

        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="mt-12 overflow-x-auto"
          data-testid="feature-matrix-table"
        >
          <table className="w-full min-w-[760px] border-collapse">
            <thead>
              <tr className="border-b border-[#0F1E3D]/15">
                <th className="text-left py-5 pr-6 eyebrow text-[#94A3B8] font-normal">{t.matrix.capability}</th>
                {HEADS.map((h, i) => (
                  <th
                    key={h}
                    className={`py-5 px-4 text-left eyebrow ${
                      i === 1 ? "text-[#0F1E3D] bg-[#C5B358]/[0.08]" : "text-[#0F1E3D]"
                    }`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {t.matrix.rows.map((row, ri) => (
                <motion.tr
                  key={row.id}
                  initial={{ opacity: 0, x: -16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.5, delay: ri * 0.06 }}
                  className="border-b border-[#E2E8F0] hover:bg-[#F8FAFC] transition-colors"
                  data-testid={`matrix-row-${ri}`}
                >
                  <td className="py-5 pr-6 text-sm font-semibold text-[#1E293B]">{row.cap}</td>
                  {row.values.map((v, vi) => {
                    const text = typeof v === "string" ? v : v.text;
                    const hl = typeof v !== "string" && v.hl;
                    return (
                      <td
                        key={vi}
                        className={`py-5 px-4 text-sm tabular-nums align-middle ${
                          vi === 1 ? "bg-[#C5B358]/[0.08]" : ""
                        }`}
                      >
                        {hl ? (
                          <span
                            className="inline-flex items-center gap-1.5 font-semibold text-[#A08C3A]"
                            data-testid={`matrix-hl-${ri}-${vi}`}
                          >
                            <Check size={13} strokeWidth={3.5} className="text-[#C5B358]" />
                            {text}
                          </span>
                        ) : (
                          <span className={vi === 1 ? "font-semibold text-[#0F1E3D]" : "text-[#64748B]"}>
                            {text}
                          </span>
                        )}
                      </td>
                    );
                  })}
                </motion.tr>
              ))}
            </tbody>
          </table>
        </motion.div>
      </div>
    </section>
  );
}